/**
 * RegisterForm - Formulaire d'inscription
 * Module auth-caritas
 *
 * Fonctionnalités :
 * - Formulaire inscription complet
 * - Validation temps réel
 * - Indicateur force mot de passe
 * - Acceptation CGU
 * - Gestion erreurs
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePasswordStrength } from '../../hooks/usePassword';
import { PasswordStrengthMeter } from '../ui/PasswordStrengthMeter';
import { LoadingSpinner } from '../ui/LoadingSpinner';

export interface RegisterFormProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

interface RegisterFormData {
  nom: string;
  prenom: string;
  email: string;
  motDePasse: string;
  confirmationMotDePasse: string;
  accepteCGU: boolean;
  organisationCible?: 'CARITAS' | 'AUTRE';
}

export function RegisterForm({ onSuccess, onError }: RegisterFormProps) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<RegisterFormData>({
    nom: '',
    prenom: '',
    email: '',
    motDePasse: '',
    confirmationMotDePasse: '',
    accepteCGU: false,
    organisationCible: 'CARITAS',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Hook pour la force du mot de passe
  const passwordStrength = usePasswordStrength(formData.motDePasse);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    // Effacer l'erreur lors de la saisie
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      // Appeler l'API d'inscription
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erreur lors de l\'inscription');
      }

      // Succès
      if (onSuccess) {
        onSuccess();
      } else {
        // Rediriger vers la page de vérification email
        navigate(`/verify-email?email=${encodeURIComponent(formData.email)}`);
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Erreur lors de l\'inscription';
      setError(errorMessage);
      if (onError) onError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const passwordsMatch = formData.motDePasse === formData.confirmationMotDePasse;
  const isFormValid =
    formData.nom.trim() &&
    formData.prenom.trim() &&
    formData.email.trim() &&
    formData.motDePasse.length >= 8 &&
    passwordsMatch &&
    formData.accepteCGU;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Nom */}
      <div>
        <label htmlFor="nom" className="block text-sm font-medium text-content-muted">
          Nom <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          id="nom"
          name="nom"
          value={formData.nom}
          onChange={handleChange}
          required
          className="mt-1 block w-full rounded-md border-border shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
          placeholder="Votre nom de famille"
        />
      </div>

      {/* Prénom */}
      <div>
        <label htmlFor="prenom" className="block text-sm font-medium text-content-muted">
          Prénom <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          id="prenom"
          name="prenom"
          value={formData.prenom}
          onChange={handleChange}
          required
          className="mt-1 block w-full rounded-md border-border shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
          placeholder="Votre prénom"
        />
      </div>

      {/* Email */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-content-muted">
          Email <span className="text-danger">*</span>
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          className="mt-1 block w-full rounded-md border-border shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
          placeholder="Votre email"
        />
      </div>

      {/* Mot de passe */}
      <div>
        <label htmlFor="motDePasse" className="block text-sm font-medium text-content-muted">
          Mot de passe <span className="text-danger">*</span>
        </label>
        <div className="relative mt-1">
          <input
            type={showPassword ? 'text' : 'password'}
            id="motDePasse"
            name="motDePasse"
            value={formData.motDePasse}
            onChange={handleChange}
            required
            className="block w-full rounded-md border-border shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
            placeholder="Minimum 8 caractères"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-content-subtle hover:text-content-muted"
          >
            {showPassword ? '🙈' : '👁️'}
          </button>
        </div>

        {/* Indicateur de force */}
        {formData.motDePasse && (
          <div className="mt-2">
            <PasswordStrengthMeter strength={passwordStrength} />
          </div>
        )}
      </div>

      {/* Confirmation mot de passe */}
      <div>
        <label htmlFor="confirmationMotDePasse" className="block text-sm font-medium text-content-muted">
          Confirmer le mot de passe <span className="text-danger">*</span>
        </label>
        <div className="relative mt-1">
          <input
            type={showConfirmPassword ? 'text' : 'password'}
            id="confirmationMotDePasse"
            name="confirmationMotDePasse"
            value={formData.confirmationMotDePasse}
            onChange={handleChange}
            required
            className="block w-full rounded-md border-border shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
            placeholder="Retapez votre mot de passe"
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-content-subtle hover:text-content-muted"
          >
            {showConfirmPassword ? '🙈' : '👁️'}
          </button>
        </div>

        {/* Message d'erreur si mots de passe ne correspondent pas */}
        {formData.confirmationMotDePasse && !passwordsMatch && (
          <p className="mt-1 text-sm text-danger">
            Les mots de passe ne correspondent pas
          </p>
        )}
      </div>

      {/* Organisation cible */}
      <div>
        <label htmlFor="organisationCible" className="block text-sm font-medium text-content-muted">
          Organisation
        </label>
        <select
          id="organisationCible"
          name="organisationCible"
          value={formData.organisationCible}
          onChange={handleChange}
          className="mt-1 block w-full rounded-md border-border shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
        >
          <option value="CARITAS">Caritas</option>
          <option value="AUTRE">Autre</option>
        </select>
      </div>

      {/* Acceptation CGU */}
      <div className="flex items-start">
        <div className="flex items-center h-5">
          <input
            id="accepteCGU"
            name="accepteCGU"
            type="checkbox"
            checked={formData.accepteCGU}
            onChange={handleChange}
            required
            className="focus:ring-primary h-4 w-4 text-primary border-border rounded"
          />
        </div>
        <div className="ml-3 text-sm">
          <label htmlFor="accepteCGU" className="font-medium text-content-muted">
            J'accepte les{' '}
            <a href="/cgu" target="_blank" className="text-primary hover:text-primary-hover">
              Conditions Générales d'Utilisation
            </a>{' '}
            <span className="text-danger">*</span>
          </label>
        </div>
      </div>

      {/* Erreur */}
      {error && (
        <div className="rounded-md bg-danger-light p-4" role="alert">
          <p className="text-sm text-danger">{error}</p>
        </div>
      )}

      {/* Bouton de soumission */}
      <div>
        <button
          type="submit"
          disabled={isLoading || !isFormValid}
          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-content-inverted bg-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <LoadingSpinner size="sm" />
              <span className="ml-2">Inscription en cours...</span>
            </>
          ) : (
            'S\'inscrire'
          )}
        </button>
      </div>

      {/* Lien vers connexion */}
      <div className="text-center text-sm">
        <span className="text-content-muted">Vous avez déjà un compte ? </span>
        <a href="/login" className="font-medium text-primary hover:text-primary-hover">
          Se connecter
        </a>
      </div>
    </form>
  );
}
